import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, companyName?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<{ success: boolean; error?: any }>;
  verifySession: () => Promise<{ isValid: boolean; needsRefresh: boolean }>;
  requestPasswordReset: (email: string) => Promise<{ error: any }>;
  confirmPasswordReset: (newPassword: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Log auth state changes for debugging
  useEffect(() => {
    console.log('🔐 === AUTH STATE UPDATE ===');
    console.log('👤 User:', { 
      id: user?.id, 
      email: user?.email,
      authenticated: !!user
    });
    console.log('🎟️ Session:', {
      exists: !!session,
      accessToken: session?.access_token ? `${session.access_token.substring(0, 20)}...` : 'null',
      refreshToken: session?.refresh_token ? `${session.refresh_token.substring(0, 20)}...` : 'null',
      expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'null',
      expiresIn: session?.expires_in || 'null'
    });
    console.log('⏳ Loading:', loading);
    console.log('============================');
  }, [user, session, loading]);

  useEffect(() => {
    console.log('🚀 === AUTH PROVIDER INITIALIZATION ===');
    
    try {
      // Set up auth state listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (event, session) => {
          console.log('🔄 AUTH EVENT:', { 
            event, 
            sessionExists: !!session,
            userId: session?.user?.id,
            userEmail: session?.user?.email
          });

          // Validate session if it exists
          if (session) {
            const now = Math.floor(Date.now() / 1000);
            const isExpired = session.expires_at ? session.expires_at < now : false;
            
            console.log('🔍 SESSION VALIDATION:', {
              isExpired,
              expiresAt: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'null',
              currentTime: new Date().toISOString(),
              secondsUntilExpiry: session.expires_at ? session.expires_at - now : 'null'
            });

            if (isExpired) {
              console.warn('⚠️ SESSION EXPIRED - attempting refresh');
              supabase.auth.refreshSession().then(({ data, error }) => {
                if (error) {
                  console.error('❌ SESSION REFRESH FAILED:', error);
                } else {
                  console.log('✅ SESSION REFRESHED SUCCESSFULLY');
                }
              });
            }
          }

          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        }
      );

      // Get initial session
      console.log('🔍 FETCHING INITIAL SESSION...');
      supabase.auth.getSession().then(({ data: { session }, error }) => {
        if (error) {
          console.error('❌ INITIAL SESSION FETCH ERROR:', error);
        } else {
          console.log('✅ INITIAL SESSION FETCHED:', {
            sessionExists: !!session,
            userId: session?.user?.id,
            userEmail: session?.user?.email
          });
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      });

      return () => {
        console.log('🧹 AUTH PROVIDER CLEANUP');
        subscription.unsubscribe();
      };
    } catch (error) {
      console.error('❌ AUTH PROVIDER SETUP ERROR:', error);
      setLoading(false);
    }
  }, []);

  const signUp = async (email: string, password: string, companyName?: string) => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          company_name: companyName || 'My Company'
        }
      }
    });

    if (error) {
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Welcome!",
        description: "Your account has been created successfully.",
      });
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      toast({
        title: "Sign in failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Welcome back!",
        description: "You have been signed in successfully.",
      });
    }

    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      toast({
        title: "Signed out",
        description: "You have been signed out successfully.",
      });
    }
  };

  const refreshSession = async () => {
    console.log('🔄 MANUAL SESSION REFRESH REQUESTED');
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('❌ MANUAL SESSION REFRESH FAILED:', error);
        return { success: false, error };
      }
      console.log('✅ MANUAL SESSION REFRESH SUCCESS');
      return { success: true };
    } catch (error) {
      console.error('❌ MANUAL SESSION REFRESH EXCEPTION:', error);
      return { success: false, error };
    }
  };

  const verifySession = async () => {
    console.log('🔍 === SESSION VERIFICATION ===');
    
    if (!session) {
      console.log('❌ No session exists');
      return { isValid: false, needsRefresh: false };
    }

    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at || 0;
    const isExpired = expiresAt < now;
    const expiresInMinutes = (expiresAt - now) / 60;

    console.log('📊 Session Status:', {
      isExpired,
      expiresAt: new Date(expiresAt * 1000).toISOString(),
      expiresInMinutes: Math.round(expiresInMinutes),
      hasAccessToken: !!session.access_token,
      hasRefreshToken: !!session.refresh_token
    });

    // Test if session works with backend
    try {
      console.log('🧪 Testing session with backend...');
      const { data, error } = await supabase.auth.getUser();
      
      if (error) {
        console.error('❌ Backend session validation failed:', error);
        return { isValid: false, needsRefresh: true };
      }

      console.log('✅ Backend session validation success:', { userId: data.user?.id });
      return { isValid: true, needsRefresh: isExpired };
    } catch (error) {
      console.error('❌ Session verification exception:', error);
      return { isValid: false, needsRefresh: true };
    }
  };

  const requestPasswordReset = async (email: string) => {
    try {
      // Call custom edge function for branded email
      const { data, error } = await supabase.functions.invoke('send-password-reset', {
        body: { email }
      });

      if (error) {
        toast({
          title: "Reset request failed",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }

      return { error: null };
    } catch (error: any) {
      toast({
        title: "Reset request failed",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }
  };

  const confirmPasswordReset = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      console.error('Password reset error:', error);
    }

    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    refreshSession,
    verifySession,
    requestPasswordReset,
    confirmPasswordReset
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};